import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

interface Expense {
  id: string;
  title: string;
  category: string;
  amount: number;
  payer_id: string | null;
  creator_id: string | null;
  trip_id: string | null;
}

interface Share {
  expense_id: string;
  user_id: string;
  amount: number;
  is_paid: boolean | null;
}

interface Payment {
  id: string;
  user_id: string | null;
  trip_id: string | null;
  amount: number;
}

interface User {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface Props {
  tripId: string;
  refreshTrigger?: number;
  onPay?: (userId: string, amount: number) => void;
}

export default function ExpenseSummary({ tripId, refreshTrigger, onPay }: Props) {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [shares, setShares] = useState<Share[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>(
    {}
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toggleUser = (userId: string) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const toggleAllUsers = (expand: boolean) => {
    const state: Record<string, boolean> = {};
    Object.keys(userTotals).forEach((id) => (state[id] = expand));
    setExpandedUsers(state);
  };
  const [allgemeineSumme, setAllgemeineSumme] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setIsRefreshing(true);

      const { data: expenseData, error: expenseError } = await supabase
        .from("trip_expenses")
        .select("*")
        .eq("trip_id", tripId);

      if (expenseError || !expenseData) {
        console.error("Fehler beim Laden der Ausgaben:", expenseError?.message);
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      const expenseIds = expenseData.map((e) => e.id);
      const { data: shareData, error: shareError } = await supabase
        .from("trip_expense_shares")
        .select("*")
        .in("expense_id", expenseIds);

      if (shareError || !shareData) {
        console.error(
          "Fehler beim Laden der Verteilungen:",
          shareError?.message
        );
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      const { data: paymentData, error: paymentError } = await supabase
        .from("trip_payments")
        .select("*")
        .eq("trip_id", tripId);

      if (paymentError || !paymentData) {
        console.error(
          "Fehler beim Laden der Zahlungen:",
          paymentError?.message
        );
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      const userIds = Array.from(
        new Set([
          ...shareData.map((s) => s.user_id),
          ...paymentData.map((p) => p.user_id),
        ])
      ).filter((id): id is string => !!id);

      const { data: userData, error: userError } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name")
        .in("id", userIds);

      if (userError || !userData) {
        console.error("Fehler beim Laden der Userdaten:", userError?.message);
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      setExpenses(expenseData);
      setShares(shareData);
      setPayments(paymentData);
      setUsers(userData);
      setLoading(false);
      setTimeout(() => setIsRefreshing(false), 500);
      setAllgemeineSumme(allgemeineZahlung);
    };

    fetchData();
  }, [tripId, refreshTrigger]);

  const participantIds = users.map((u) => u.id);
  const userTotals: Record<string, { owed: number; paid: number }> = {};

  // 1. Schulden aus shares sammeln
  shares.forEach((s) => {
    if (!userTotals[s.user_id]) {
      userTotals[s.user_id] = { owed: 0, paid: 0 };
    }
    userTotals[s.user_id].owed += s.amount;
  });

  // 2. Zahlungen berücksichtigen (auch Allgemeine!)
  let allgemeineZahlung = 0;
  payments.forEach((p) => {
    if (!p.user_id) {
      allgemeineZahlung += p.amount;
    } else {
      if (!userTotals[p.user_id]) {
        userTotals[p.user_id] = { owed: 0, paid: 0 };
      }
      userTotals[p.user_id].paid += p.amount;
    }
  });

  // 3. Allgemeine Zahlung gleichmäßig verteilen (neuer Block!)
  if (allgemeineZahlung > 0 && participantIds.length > 0) {
    const anteil = parseFloat(
      (allgemeineZahlung / participantIds.length).toFixed(2)
    );
    participantIds.forEach((uid) => {
      if (!userTotals[uid]) {
        userTotals[uid] = { owed: 0, paid: 0 };
      }
      userTotals[uid].paid += anteil;
    });
  }

  // 4. Gutschriften aus bezahlten Ausgaben (wie bisher)
  expenses.forEach((exp) => {
    const payerId = exp.payer_id;
    if (!payerId) return;
    if (!userTotals[payerId]) {
      userTotals[payerId] = { owed: 0, paid: 0 };
    }
    userTotals[payerId].paid += exp.amount;
  });

  // Saldo-Berechnung: Nur Einzahlungen ohne user_id (→ Reisekasse) + Ausgaben, die aus Reisekasse bezahlt wurden
  const totalEinzahlungen = payments.reduce((sum, p) => sum + p.amount, 0);
  const reisekasseAusgaben = expenses.filter((e) => !e.payer_id);
  const totalAusgaben = reisekasseAusgaben.reduce(
    (sum, e) => sum + e.amount,
    0
  );
  const saldoReisekasse = totalEinzahlungen - totalAusgaben;

  if (loading) return <div className="text-slate-600 text-sm px-1">Lade Ausgaben…</div>;

  return (
    <div className="mt-6">
      {Object.entries(userTotals).length === 0 ? (
        <div className="text-center py-10">
          <p className="text-slate-500 text-sm">Noch keine Daten zur Verteilung vorhanden.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              Detailübersicht pro Teilnehmer
            </span>
            <div className="flex gap-3 text-xs">
              <button
                onClick={() => toggleAllUsers(true)}
                className="text-sky-400 hover:text-sky-300 transition-colors"
              >
                Alle öffnen
              </button>
              <button
                onClick={() => toggleAllUsers(false)}
                className="text-sky-400 hover:text-sky-300 transition-colors"
              >
                Alle schließen
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-500 mb-4">
            <span className="text-red-400">Rot</span> = schuldet der Kasse ·{" "}
            <span className="text-emerald-400">Grün</span> = hat Guthaben
          </p>

          {isRefreshing && (
            <div className="h-0.5 mb-3 bg-gradient-to-r from-sky-400 via-sky-500 to-sky-400 animate-pulse rounded" />
          )}

          <div className="space-y-2">
            {Object.entries(userTotals).map(([userId, { owed, paid }]) => {
              const user = users.find((u) => u.id === userId);
              if (!user) return null;

              const isExpanded = expandedUsers[userId] ?? false;
              const name =
                user.first_name || user.last_name
                  ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                  : user.email;

              const userExpenses = shares.filter((s) => s.user_id === userId);
              const balance = owed - paid;

              return (
                <div key={userId} className="bg-[#161b27] rounded-xl border border-[#2a3347] overflow-hidden">
                  <div className="px-4 py-3 flex justify-between items-center">
                    <button
                      onClick={() => toggleUser(userId)}
                      className="flex-1 flex justify-between items-center text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="text-sm text-slate-100">{name}</span>
                      <span className="flex items-center gap-2">
                        <strong className={`text-sm tabular-nums ${
                          balance > 0 ? "text-red-400" : balance < 0 ? "text-emerald-400" : "text-slate-500"
                        }`}>
                          {balance > 0 ? "+" : ""}{balance.toFixed(2)}
                        </strong>
                        <span className="text-slate-600 text-[10px]">{isExpanded ? "▲" : "▼"}</span>
                      </span>
                    </button>
                    {balance > 0.01 && onPay && (
                      <button
                        onClick={() => onPay(userId, parseFloat(balance.toFixed(2)))}
                        className="ml-3 text-xs px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white transition-colors flex-shrink-0"
                      >
                        💳 Einzahlen
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-[#1e2639]">
                      <div className="text-[10px] text-slate-600 mt-3 mb-2">
                        Schulden: {owed.toFixed(2)} · Zahlungen: {paid.toFixed(2)}
                      </div>
                      <div className="space-y-1">
                        {userExpenses.map((s, i) => {
                          const expense = expenses.find((e) => e.id === s.expense_id);
                          return (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-slate-400">{expense?.title ?? "Unbekannt"}</span>
                              <span className="text-slate-300 tabular-nums">{s.amount.toFixed(2)}</span>
                            </div>
                          );
                        })}
                        {payments
                          .filter((p) => String(p.user_id) === String(userId))
                          .map((p, i) => (
                            <div key={`payment-${i}`} className="flex justify-between text-xs">
                              <span className="text-emerald-400/70">Zahlung</span>
                              <span className="text-emerald-400 tabular-nums">-{p.amount.toFixed(2)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
